import React from "react";
import { base44 } from "@/api/base44Client";
import BillingService from "@/lib/billingService";
import { clearStoredAccessToken, hasStoredAccessToken } from "@/lib/authToken";

const DEFAULT_STATE = Object.freeze({
  ready: false,
  user: null,
  role: "Guest",
  tier: "Guest",
  isGuest: true,
  isLoggedIn: false,
  isFree: false,
  isPremium: false,
  isAdmin: false,
  premiumAccessAll: false,
  premiumPages: [],
  previewAsUser: false,
});

let cachedState = null;
let cachedStateKey = null;
let pendingStateRequest = null;

function getStoredImpersonatedUser() {
  try {
    return JSON.parse(localStorage.getItem("impersonatingUser") || "null");
  } catch {
    return null;
  }
}

function buildMenuState(user, premiumEntitlement, previewAsUser, options = {}) {
  const realAdmin = BillingService.isAdminUser(user);
  const isAdmin = realAdmin && (!previewAsUser || options.impersonating);
  const isLoggedIn = !!user;
  const suppressPremium = previewAsUser && !options.impersonating;
  const premiumAccessAll = !!premiumEntitlement?.all && !suppressPremium;
  const premiumPages = suppressPremium ? [] : (premiumEntitlement?.pages || []);
  const isPremium = isAdmin || premiumAccessAll;
  const role = isAdmin ? "Admin" : isPremium ? "Premium" : isLoggedIn ? "Free" : "Guest";

  return {
    ready: true,
    user,
    role,
    tier: role,
    isGuest: !isLoggedIn,
    isLoggedIn,
    isFree: isLoggedIn && !isPremium && !isAdmin,
    isPremium,
    isAdmin,
    premiumAccessAll,
    premiumPages,
    previewAsUser,
  };
}

export default function useMenuUserState(initialUser = null) {
  const [state, setState] = React.useState(() => initialUser
    ? buildMenuState(initialUser, { all: BillingService.isAdminUser(initialUser), pages: [] }, localStorage.getItem("previewAsUser") === "true")
    : DEFAULT_STATE
  );

  const resolveState = React.useCallback(async () => {
    const previewAsUser = localStorage.getItem("previewAsUser") === "true";
    const impersonatedUser = previewAsUser ? getStoredImpersonatedUser() : null;
    const stateKey = `${previewAsUser}:${impersonatedUser?.id || ""}:${initialUser?.id || ""}`;

    if (!initialUser && cachedState && cachedStateKey === stateKey) {
      setState(cachedState);
      return;
    }

    try {
      if (!initialUser && !hasStoredAccessToken()) {
        const guestState = { ...DEFAULT_STATE, ready: true, previewAsUser };
        cachedState = guestState;
        cachedStateKey = stateKey;
        setState(guestState);
        return;
      }

      pendingStateRequest = (async () => {
        const realUser = initialUser || await base44.auth.me();
        const effectiveUser = impersonatedUser || realUser;
        const premiumEntitlement = impersonatedUser?.email
          ? await BillingService.getPremiumEntitlements(impersonatedUser.email)
          : BillingService.isAdminUser(realUser)
            ? { all: true, pages: [] }
            : realUser?.email
              ? await BillingService.getPremiumEntitlements(realUser.email)
              : { all: false, pages: [] };
        return buildMenuState(effectiveUser, premiumEntitlement, previewAsUser, { impersonating: !!impersonatedUser });
      })();

      const nextState = await pendingStateRequest;
      cachedState = nextState;
      cachedStateKey = stateKey;
      setState(nextState);
    } catch {
      clearStoredAccessToken();
      const guestState = { ...DEFAULT_STATE, ready: true, previewAsUser };
      cachedState = guestState;
      cachedStateKey = stateKey;
      setState(guestState);
    } finally {
      pendingStateRequest = null;
    }
  }, [initialUser]);

  React.useEffect(() => {
    resolveState();
    window.addEventListener("previewAsUserChanged", resolveState);
    return () => window.removeEventListener("previewAsUserChanged", resolveState);
  }, [resolveState]);

  return React.useMemo(() => state, [state]);
}